/**
 * TOAST CONFIG v2.0 - ULTRA OPTIMIZED
 * ✅ React.memo en componentes
 * ✅ useMemo para configuraciones
 * ✅ Configuración personalizada para react-hot-toast
 */
import { memo, useMemo } from 'react';
import { Toaster } from 'react-hot-toast';
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  XCircleIcon, 
  InformationCircleIcon,
  FireIcon
} from '@heroicons/react/24/solid';

// ═══════════════════════════════════════════════
// TOASTER PERSONALIZADO
// ═══════════════════════════════════════════════

export const CustomToaster = memo(function CustomToaster() {
  const toastOptions = useMemo(() => ({
    duration: 3000,
    style: {
      background: '#1F2937',
      color: '#fff',
      borderRadius: '12px',
      border: '1px solid #374151',
      padding: '12px 16px',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(8px)',
    },
    
    success: {
      duration: 3000,
      style: {
        background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
        color: '#fff',
        border: '1px solid rgba(16, 185, 129, 0.3)',
        boxShadow: '0 10px 25px rgba(16, 185, 129, 0.3)',
      },
      iconTheme: {
        primary: '#fff',
        secondary: '#10B981',
      },
    },
    
    error: {
      duration: 4000,
      style: {
        background: 'linear-gradient(135deg, #DC2626 0%, #EF4444 100%)',
        color: '#fff',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        boxShadow: '0 10px 25px rgba(239, 68, 68, 0.3)',
      },
      iconTheme: {
        primary: '#fff',
        secondary: '#EF4444',
      },
    },
    
    loading: {
      style: {
        background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
        color: '#fff',
        border: '1px solid rgba(139, 92, 246, 0.3)',
        boxShadow: '0 10px 25px rgba(139, 92, 246, 0.3)',
      },
    },
    
    custom: {
      duration: 3000,
    },
  }), []);

  return (
    <Toaster
      position="top-center"
      reverseOrder={false}
      gutter={8}
      containerStyle={{
        top: 20,
      }}
      toastOptions={toastOptions}
    />
  );
});

// ═══════════════════════════════════════════════
// TOAST OPTIONS (para uso con toast())
// ═══════════════════════════════════════════════

export const successToast = () => ({
  icon: <CheckCircleIcon className="w-5 h-5 text-green-400" />,
  style: {
    background: 'rgba(16, 185, 129, 0.1)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(16, 185, 129, 0.3)',
    color: '#10B981',
  },
});

export const errorToast = () => ({
  icon: <XCircleIcon className="w-5 h-5 text-red-400" />,
  style: {
    background: 'rgba(239, 68, 68, 0.1)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    color: '#EF4444',
  },
});

export const warningToast = () => ({
  icon: <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400" />,
  style: {
    background: 'rgba(251, 191, 36, 0.1)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(251, 191, 36, 0.3)',
    color: '#FBBF24',
  },
});

export const infoToast = () => ({
  icon: <InformationCircleIcon className="w-5 h-5 text-blue-400" />,
  style: {
    background: 'rgba(59, 130, 246, 0.1)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(59, 130, 246, 0.3)',
    color: '#3B82F6',
  },
});

export const cookingToast = () => ({
  icon: <FireIcon className="w-5 h-5 text-orange-400" />,
  style: {
    background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.2) 0%, rgba(234, 88, 12, 0.2) 100%)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(249, 115, 22, 0.3)',
    color: '#F97316',
  },
  duration: 4000,
});

// ═══════════════════════════════════════════════
// TOAST VARIANTS (para uso con toast.custom())
// ═══════════════════════════════════════════════

const SuccessToastVariant = memo(function SuccessToastVariant({ message }) {
  return (
    <div className="flex items-center gap-3 bg-green-900/90 backdrop-blur-md border border-green-500/30 rounded-xl px-4 py-3 shadow-xl shadow-green-900/50">
      <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0" />
      <span className="text-sm font-medium text-white">{message}</span>
    </div>
  );
});

const ErrorToastVariant = memo(function ErrorToastVariant({ message }) {
  return (
    <div className="flex items-center gap-3 bg-red-900/90 backdrop-blur-md border border-red-500/30 rounded-xl px-4 py-3 shadow-xl shadow-red-900/50">
      <XCircleIcon className="w-5 h-5 text-red-400 flex-shrink-0" />
      <span className="text-sm font-medium text-white">{message}</span>
    </div>
  );
});

const WarningToastVariant = memo(function WarningToastVariant({ message }) {
  return (
    <div className="flex items-center gap-3 bg-yellow-900/90 backdrop-blur-md border border-yellow-500/30 rounded-xl px-4 py-3 shadow-xl shadow-yellow-900/50">
      <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 flex-shrink-0" />
      <span className="text-sm font-medium text-white">{message}</span>
    </div>
  );
});

const InfoToastVariant = memo(function InfoToastVariant({ message }) {
  return (
    <div className="flex items-center gap-3 bg-blue-900/90 backdrop-blur-md border border-blue-500/30 rounded-xl px-4 py-3 shadow-xl shadow-blue-900/50">
      <InformationCircleIcon className="w-5 h-5 text-blue-400 flex-shrink-0" />
      <span className="text-sm font-medium text-white">{message}</span>
    </div>
  );
});

const CookingToastVariant = memo(function CookingToastVariant({ message }) {
  return (
    <div className="flex items-center gap-3 bg-orange-900/90 backdrop-blur-md border border-orange-500/30 rounded-xl px-4 py-3 shadow-xl shadow-orange-900/50 animate-pulse">
      <FireIcon className="w-5 h-5 text-orange-400 flex-shrink-0 animate-bounce" />
      <span className="text-sm font-medium text-white">{message}</span>
    </div>
  );
});

export const ToastVariants = {
  success: (message) => <SuccessToastVariant message={message} />,
  error: (message) => <ErrorToastVariant message={message} />,
  warning: (message) => <WarningToastVariant message={message} />,
  info: (message) => <InfoToastVariant message={message} />,
  cooking: (message) => <CookingToastVariant message={message} />,
};

// ═══════════════════════════════════════════════
// EXPORT DEFAULT
// ═══════════════════════════════════════════════

export default {
  CustomToaster,
  ToastVariants,
  successToast,
  errorToast,
  warningToast,
  infoToast,
  cookingToast,
};
